import React, { useEffect, useState } from 'react';
import { CheckCircle, Loader2, MapPin, Phone, ShieldCheck, UserCircle2, X, Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import {
  BUSINESS,
  capturePaypalOrder,
  cartLineErrors,
  computeDeposit,
  createCartOrder,
  createSpecialOrder,
  DEFAULT_MAINTENANCE_MESSAGE,
  formatPrice,
  getPaymentConfig,
} from '../config';
import { useAuth } from '../context/AuthContext';

// Délai estimé lisible à partir de delivery_days_min/max (même logique
// que ReservationModal — disponible immédiatement vs fourchette de jours).
function formatDelay(product) {
  const { delivery_days_min: dMin, delivery_days_max: dMax, availability, condition } = product;
  if (dMin === 0 || availability === 'stock_meteris' || condition === 'reconditionne') {
    return 'Disponible immédiatement, à retirer en boutique.';
  }
  if (dMin && dMax) {
    return dMin === dMax
      ? `Délai estimé : ${dMax} jours.`
      : `Délai estimé : ${dMin} à ${dMax} jours.`;
  }
  if (dMax) return `Délai estimé : ${dMax} jours.`;
  return 'Délai à confirmer après commande.';
}

// Échéance de retrait d'une réservation, à partir de l'`expires_at` renvoyé par
// le serveur. Ce n'est pas de la décoration : passé cette heure, le balayage
// `release_expired_reservations` remet les articles en vente. Un client qui
// croit sa réservation tenue plusieurs jours se déplacerait pour rien.
function formatPickupDeadline(expiresAt) {
  if (!expiresAt) return '';
  const deadline = new Date(expiresAt);
  if (Number.isNaN(deadline.getTime())) return '';
  // Heure locale, écrite comme partout sur le site (« 18h00 »), jamais « 18:00 ».
  const heure = `${deadline.getHours()}h${String(deadline.getMinutes()).padStart(2, '0')}`;
  const now = new Date();
  if (deadline.toDateString() === now.toDateString()) return `aujourd'hui avant ${heure}`;
  const jour = new Intl.DateTimeFormat('fr-FR', {
    weekday: 'long', day: 'numeric', month: 'long',
  }).format(deadline);
  return `avant ${jour}, ${heure}`;
}

// ── Sous-composant : formulaire de paiement Stripe (PaymentElement) ──────
// Monté uniquement à l'intérieur d'<Elements>, donc useStripe/useElements
// sont garantis disponibles. Gère son propre loading/error (états locaux).
function StripePaymentForm({ depositAmount, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  const handlePay = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    setPayError('');
    setPaying(true);
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // `/commande/confirmation` est désormais une vraie page (cf App.js et
          // `pages/OrderConfirmation.js`). Avec `redirect: 'if_required'`
          // ci-dessous, elle n'est atteinte que par une carte exigeant une
          // authentification 3DS ; la page lit alors `redirect_status` et confirme
          // au client, au lieu de le laisser sur une zone vide.
          return_url: `${window.location.origin}/commande/confirmation`,
        },
        redirect: 'if_required',
      });
      if (error) {
        setPayError(error.message || "Le paiement n'a pas pu être confirmé.");
        setPaying(false);
        return;
      }
      // Paiement validé sans redirection (carte sans 3DS) — on affiche le succès.
      if (onPaid) onPaid();
    } catch (err) {
      setPayError(err.message || 'Erreur lors du paiement.');
      setPaying(false);
    }
  };

  return (
    <form onSubmit={handlePay} className="space-y-4" data-testid="special-order-payment-form">
      <PaymentElement />

      {payError && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-3 text-sm text-red-500" data-testid="special-order-payment-error" role="alert">
          {payError}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || !elements || paying}
        className="w-full bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="special-order-pay-button"
      >
        {paying ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Paiement en cours…
          </>
        ) : (
          <>
            <Lock className="w-4 h-4" />
            Payer {formatPrice(depositAmount)}
          </>
        )}
      </button>
    </form>
  );
}

/**
 * Tunnel de commande, en deux entrées :
 *   - `product`   : commande d'un produit unique depuis sa fiche ;
 *   - `cartItems` : commande multi-lignes depuis la page panier.
 *
 * `cartWarranties` accompagne `cartItems` : les Garanties Atelier choisies au
 * panier, sous la forme `{target_product_id, years, price, service_label}`. Le
 * prix et le libellé ne servent QU'À l'affichage — seuls le produit couvert et
 * la durée partent au serveur, qui applique le barème au prix relu en base.
 *
 * Le MODE DE RÈGLEMENT n'est jamais décidé ici. Le serveur relit les prix et les
 * disponibilités en base et renvoie `mode` : `reservation` (mise de côté, on
 * règle au comptoir) ou `online_payment` (total payé en ligne). La modale
 * n'affiche que ce qu'il a répondu — un pronostic local sur l'instantané du
 * panier se tromperait dès qu'une fiche a bougé depuis l'ajout.
 *
 * `onSuccess` signale une commande DÉFINITIVEMENT acquise : c'est lui qui vide
 * le panier côté page. Il part à la confirmation en mode réservation, mais
 * seulement après capture confirmée en paiement en ligne — vider avant
 * laisserait un client sans panier ET sans commande s'il abandonne sur PayPal.
 *
 * `onCartInvalid` remonte le détail ligne par ligne d'un refus 409
 * `cart_invalid` à la page panier, seul endroit où le client peut corriger.
 */
export default function SpecialOrderModal({
  product, cartItems, cartWarranties, maintenance, onClose, onSuccess, onCartInvalid,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [accepted, setAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Verdict du serveur : mode de règlement, montants, référence et échéance de
  // retrait. Tant qu'il n'a pas répondu, ces valeurs restent nulles et
  // l'affichage se rabat sur l'instantané — jamais l'inverse.
  const [orderMode, setOrderMode] = useState(null);
  const [serverTotal, setServerTotal] = useState(null);
  const [amountToPay, setAmountToPay] = useState(null);
  const [reservationId, setReservationId] = useState('');
  const [pickupDeadline, setPickupDeadline] = useState('');

  // Montant qui était à l'écran quand le client a validé, et prise d'acte d'un
  // éventuel écart avec le montant recalculé en base. Le client ne doit jamais
  // arriver devant un moyen de paiement portant une somme qu'il n'a pas vue.
  const [quotedTotal, setQuotedTotal] = useState(null);
  const [totalAcknowledged, setTotalAcknowledged] = useState(false);

  // Config paiement : on récupère les identifiants providers au montage. Tant
  // qu'on n'a rien, on reste en chargement. Si AUCUN provider (ni Stripe ni
  // PayPal) → mode dormant (message « bientôt disponible »).
  const [configLoading, setConfigLoading] = useState(true);
  const [publishableKey, setPublishableKey] = useState(null);
  const [paypalClientId, setPaypalClientId] = useState(null);

  // Provider retenu après createSpecialOrder ('stripe' | 'paypal').
  const [provider, setProvider] = useState(null);

  // Stripe : promesse loadStripe (créée une fois la clé connue) + client_secret
  // renvoyé par createSpecialOrder. Les deux ⇒ on monte <Elements>.
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);

  // PayPal : id de la commande PayPal renvoyé par createSpecialOrder, réutilisé
  // tel quel dans createOrder des PayPalButtons (l'ordre est créé côté backend).
  const [paypalOrderId, setPaypalOrderId] = useState(null);

  const isMaintenanceActive = Boolean(maintenance?.enabled);
  const maintenanceMessage = maintenance?.message || DEFAULT_MAINTENANCE_MESSAGE;
  const isAuthenticated = Boolean(user);
  const hasCompleteProfile = Boolean(user?.profile_completed);

  const lines = Array.isArray(cartItems) ? cartItems : [];
  const isCart = lines.length > 0;
  // Retour après un détour par le profil ou l'inscription : là d'où l'on vient.
  const returnPath = isCart ? '/panier' : `/produit/${product?.id}`;
  const nextProfilePath = `/compte/profil?next=${encodeURIComponent(returnPath)}`;

  // Garanties Atelier choisies au panier. Elles n'ont ni stock ni quantité
  // propre : une garantie couvre TOUTES les unités de sa ligne, donc son montant
  // suit la quantité du produit couvert. Une garantie dont le produit n'est plus
  // là n'est pas affichable — et n'a rien à faire dans un total.
  const warranties = (Array.isArray(cartWarranties) ? cartWarranties : [])
    .map((warranty) => {
      const covered = lines.find((line) => line.product_id === warranty.target_product_id);
      return {
        ...warranty,
        product_name: covered?.name || '',
        qty: Number(covered?.qty) || 0,
      };
    })
    .filter((warranty) => warranty.qty > 0 && Number(warranty.price) > 0);

  const warrantyTotal = Math.round(warranties.reduce(
    (sum, warranty) => sum + Number(warranty.price) * warranty.qty, 0) * 100) / 100;

  // Somme des lignes telles qu'elles sont affichées : c'est l'instantané du
  // panier, pas un montant ferme. Dès que le serveur a répondu, c'est SON total
  // qui s'affiche — lui seul a relu les prix en base.
  const snapshotTotal = Math.round((lines.reduce(
    (sum, line) => sum + (Number(line.price) || 0) * (Number(line.qty) || 0), 0)
    + warrantyTotal) * 100) / 100;
  const fallbackTotal = isCart ? snapshotTotal : computeDeposit(product?.price);
  const displayTotal = serverTotal !== null ? serverTotal : fallbackTotal;
  const depositAmount = amountToPay !== null ? amountToPay : fallbackTotal;
  const delayText = product ? formatDelay(product) : '';

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getPaymentConfig();
        if (!active) return;
        setPublishableKey(data?.stripe_publishable_key || null);
        setPaypalClientId(data?.paypal_client_id || null);
      } catch {
        if (active) {
          setPublishableKey(null);
          setPaypalClientId(null);
        }
      } finally {
        if (active) setConfigLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Rien à commander : ni fiche produit, ni ligne de panier. Placée après les
  // hooks, jamais avant — une sortie anticipée au-dessus rendrait `useEffect`
  // conditionnel.
  //
  // `success` est dans la condition parce que la page panier vide le panier sur
  // `onSuccess` : `cartItems` devient vide dans le même lot de rendu que la
  // confirmation. Sans lui, la modale se réduirait à `null` à l'instant précis
  // où elle a quelque chose à annoncer.
  if (!product && !isCart && !success) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isMaintenanceActive) {
      setError(maintenanceMessage);
      return;
    }
    if (isAuthenticated && !hasCompleteProfile) {
      navigate(nextProfilePath);
      return;
    }
    if (!accepted) {
      setError('Veuillez accepter les conditions générales de vente pour continuer.');
      return;
    }

    setError('');
    setLoading(true);
    // Montant que le client avait sous les yeux en validant : c'est à celui-là
    // qu'on comparera le montant recalculé en base.
    const quoted = fallbackTotal;
    try {
      const contact = isAuthenticated ? {} : form;
      const res = isCart
        ? await createCartOrder(lines, contact, warranties)
        : await createSpecialOrder(product.id, contact);

      // `total` est le montant de la commande, `deposit_amount` ce qu'il y a à
      // régler en ligne (0 en réservation). Le flux mono-produit ne renvoie que
      // le second : il sert alors de total.
      const total = Number(res?.total ?? res?.deposit_amount ?? 0);
      const safeTotal = Number.isFinite(total) ? total : 0;

      // Le serveur a tranché : mise de côté, règlement au comptoir. Aucun
      // paiement à préparer, la commande est acquise dès maintenant.
      if (res?.mode === 'reservation') {
        setQuotedTotal(quoted);
        setServerTotal(safeTotal);
        setOrderMode('reservation');
        setReservationId(res?.reservation_id || '');
        setPickupDeadline(formatPickupDeadline(res?.expires_at));
        setSuccess(true);
        if (onSuccess) onSuccess();
        return;
      }

      // TOUTES les vérifications d'abord, aucun état ensuite : un échec
      // d'initialisation ne doit pas laisser à l'écran le total d'une commande
      // qui n'ira nulle part. `paypalClientId` / `publishableKey` sont exigés
      // ici parce que sans eux le SDK correspondant ne peut pas se monter — et
      // qu'un provider armé à vide bloquerait le client sur un écran inerte.
      const resProvider = res?.provider || (res?.client_secret ? 'stripe' : null);
      const usable = (resProvider === 'paypal' && res?.paypal_order_id && paypalClientId)
        || (resProvider === 'stripe' && res?.client_secret && publishableKey);
      if (!usable) {
        throw new Error("Le paiement n'a pas pu être initialisé. Contactez la boutique.");
      }

      const due = Number(res?.deposit_amount ?? res?.total ?? 0);
      setQuotedTotal(quoted);
      setServerTotal(safeTotal);
      setAmountToPay(Number.isFinite(due) ? due : 0);
      setOrderMode('online_payment');
      setProvider(resProvider);
      if (resProvider === 'paypal') {
        setPaypalOrderId(res.paypal_order_id);
      } else {
        setStripePromise(loadStripe(publishableKey));
        setClientSecret(res.client_secret);
      }
    } catch (err) {
      // Refus panier : le détail ligne par ligne se lit et se corrige SUR LES
      // LIGNES du panier, pas ici — la page les affiche au niveau des articles
      // fautifs et referme le tunnel. Les autres échecs (500, réseau,
      // maintenance) restent un message dans la modale.
      const refusedLines = cartLineErrors(err);
      if (refusedLines.length > 0 && onCartInvalid) {
        onCartInvalid(refusedLines);
      } else {
        setError(err.message || 'Erreur lors de la création de la commande.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePaid = () => {
    setSuccess(true);
    if (onSuccess) onSuccess();
  };

  // Étape paiement atteinte dès qu'un provider est armé : Stripe (Elements prêts)
  // ou PayPal (ordre créé côté backend).
  const showStripeStep = provider === 'stripe' && Boolean(clientSecret && stripePromise);
  const showPaypalStep = provider === 'paypal' && Boolean(paypalOrderId);
  const paymentReady = showStripeStep || showPaypalStep;

  // Écart entre le montant validé par le client et celui recalculé en base. Le
  // serveur ne renvoie pas les lignes retenues : la modale ne peut pas corriger
  // les sous-totaux affichés, elle doit donc annoncer l'écart explicitement. En
  // paiement en ligne, il est BLOQUANT — on ne présente pas un moyen de paiement
  // portant une somme que le client n'a pas acceptée.
  const totalChanged = quotedTotal !== null && serverTotal !== null
    && Math.round(quotedTotal * 100) !== Math.round(serverTotal * 100);
  const needsTotalAck = totalChanged && !totalAcknowledged;
  const showPaymentStep = paymentReady && !needsTotalAck;

  // Une commande existe côté serveur et attend son règlement : fermer par
  // inadvertance (clic sur le fond) la ferait perdre de vue, et le client
  // recommanderait — en réservation, sa seconde tentative buterait sur des
  // produits que sa PREMIÈRE commande retient déjà.
  const orderPending = Boolean(provider) && !success;

  // Mode dormant : aucun provider exposé par le backend. Il ne concerne que la
  // commande d'un produit à payer. Un panier peut se conclure en réservation
  // gratuite, qui n'a besoin d'AUCUN provider : le lui interdire ici fermerait
  // la boutique pour une raison qui ne la regarde pas. S'il s'avère payant, le
  // backend refuse avec `deposit_unavailable` et le message s'affiche.
  const hasAnyProvider = Boolean(publishableKey || paypalClientId);
  const showDormant = !hasAnyProvider && !isCart;

  const isReservation = orderMode === 'reservation';
  const modalTitle = success
    ? (isReservation ? 'Réservation confirmée' : 'Commande confirmée')
    : (isCart ? 'Ma commande' : 'Commander ce produit');

  // Communs aux deux écrans de confirmation : on retire au même comptoir.
  const contactBoutique = (
    <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-4 text-left space-y-3 mb-6">
      <div className="flex items-center gap-3">
        <MapPin className="w-5 h-5 text-secondary shrink-0" />
        <div>
          <p className="text-xs text-slate-600">Adresse</p>
          <p className="text-sm font-semibold text-primary">{BUSINESS.adresse}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Phone className="w-5 h-5 text-secondary shrink-0" />
        <div>
          <p className="text-xs text-slate-600">Téléphone</p>
          <p className="text-sm font-semibold text-primary">{BUSINESS.telephone}</p>
        </div>
      </div>
    </div>
  );

  const boutonFermer = (
    <button
      type="button"
      onClick={onClose}
      className="w-full bg-slate-100 text-slate-700 border border-slate-200 font-bold uppercase tracking-wider rounded-sm py-3 text-sm hover:bg-slate-200 transition-colors"
    >
      Fermer
    </button>
  );

  // Sortie explicite de l'étape paiement. Sans elle, la seule façon de quitter
  // l'écran est le clic sur le fond — devenu inerte, justement pour éviter les
  // sorties accidentelles. On dit aussi dans quel état on laisse la commande.
  const sortiePaiement = (
    <div className="pt-3 border-t border-slate-200 space-y-2">
      <button
        type="button"
        onClick={onClose}
        data-testid="special-order-payment-exit"
        className="w-full bg-slate-100 text-slate-700 border border-slate-200 font-bold uppercase tracking-wider rounded-sm py-3 text-sm hover:bg-slate-200 transition-colors"
      >
        Régler plus tard
      </button>
      <p className="text-[10px] text-slate-500 leading-relaxed text-center">
        Votre commande est enregistrée mais n'est pas réglée. Ne la repassez pas :
        contactez la boutique au {BUSINESS.telephone} pour la régler ou l'annuler.
      </p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="special-order-modal">
      {/* Fond inerte tant qu'une commande attend son règlement : cf `orderPending`. */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        data-testid="special-order-backdrop"
        onClick={orderPending ? undefined : onClose}
      />
      <div className="relative bg-white rounded-sm border border-slate-200 w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-heading font-semibold text-lg text-primary uppercase tracking-wider">
            {modalTitle}
          </h3>
          <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-slate-100 transition-colors" data-testid="special-order-modal-close">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {success && isReservation ? (
          /* ── Écran succès : réservation, règlement au comptoir ── */
          <div className="p-6 text-center" data-testid="special-order-reservation">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h4 className="font-heading font-semibold text-xl text-primary uppercase tracking-wider mb-2">
              Réservation confirmée
            </h4>
            <p className="text-slate-700 text-sm mb-4">
              Vos articles sont mis de côté et vous attendent en boutique. Le règlement
              se fait au comptoir, au retrait : venez sans rendez-vous du lundi au
              vendredi, {BUSINESS.creneauRetrait}. Vous recevez la confirmation par email.
            </p>

            {/* L'échéance n'est pas un détail : passé cette heure, le balayage
                serveur remet les articles en vente. */}
            {pickupDeadline && (
              <p
                className="bg-amber-50 border border-amber-300 rounded-sm p-3 text-sm text-amber-900 mb-6"
                data-testid="special-order-pickup-deadline"
              >
                À retirer {pickupDeadline} : passé cette heure, les articles repartent
                à la vente et votre réservation est annulée.
              </p>
            )}

            <div className="bg-secondary/5 border border-secondary/15 rounded-sm p-4 mb-6 text-left">
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-secondary">À régler au comptoir</span>
                <span className="text-secondary" data-testid="special-order-reservation-total">
                  {formatPrice(displayTotal)}
                </span>
              </div>
              {reservationId && (
                <p className="text-xs text-slate-600 mt-2">Référence : {reservationId}</p>
              )}
              {/* Rien n'est payé à ce stade : l'écart s'annonce, il ne se fait
                  pas accepter — le client réglera le montant juste au comptoir. */}
              {totalChanged && (
                <p className="text-xs text-amber-800 mt-2" data-testid="special-order-total-changed-note">
                  Montant revérifié en boutique : {formatPrice(quotedTotal)} →{' '}
                  {formatPrice(serverTotal)}.
                </p>
              )}
            </div>

            {contactBoutique}
            {boutonFermer}
          </div>
        ) : success ? (
          /* ── Écran succès : commande payée en ligne ── */
          <div className="p-6 text-center" data-testid="special-order-success">
            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h4 className="font-heading font-semibold text-xl text-primary uppercase tracking-wider mb-2">
              Commande confirmée
            </h4>
            <p className="text-slate-700 text-sm mb-6">
              Paiement reçu, votre commande est confirmée. Nous la préparons et vous
              préviendrons par email dès qu'elle est prête à retirer, sans rendez-vous,
              du lundi au vendredi, {BUSINESS.creneauRetrait}.
            </p>

            {contactBoutique}
            {boutonFermer}
          </div>
        ) : configLoading ? (
          /* ── Chargement config paiement ── */
          <div className="p-10 flex items-center justify-center" data-testid="special-order-loading">
            <Loader2 className="w-6 h-6 text-secondary animate-spin" />
          </div>
        ) : showDormant ? (
          /* ── Mode dormant : paiement non disponible ── */
          <div className="p-6 text-center" data-testid="special-order-unavailable">
            <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-4 text-sm text-slate-700 mb-6">
              La commande en ligne sera bientôt disponible. Contactez la boutique
              {isCart ? ' pour votre sélection.' : ' pour ce produit.'}
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-left space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-secondary shrink-0" />
                <div>
                  <p className="text-xs text-slate-600">Téléphone</p>
                  <p className="text-sm font-semibold text-primary">{BUSINESS.telephone}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-secondary shrink-0" />
                <div>
                  <p className="text-xs text-slate-600">Adresse</p>
                  <p className="text-sm font-semibold text-primary">{BUSINESS.adresse}</p>
                </div>
              </div>
            </div>
            <a
              href={`tel:${BUSINESS.telephoneRaw}`}
              className="block w-full bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3 text-sm text-center transition-colors"
            >
              Appeler la boutique
            </a>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {isCart ? (
              /* Récap panier : une ligne par article, avec son sous-total */
              <div
                className="bg-slate-50 rounded-sm border border-slate-200 divide-y divide-slate-200"
                data-testid="special-order-cart-lines"
              >
                {lines.map((line) => (
                  <div
                    key={line.product_id}
                    className="flex items-start justify-between gap-3 p-3"
                    data-testid={`special-order-cart-line-${line.product_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-primary line-clamp-2">{line.name}</p>
                      <p className="text-xs text-slate-600">
                        Quantité : {line.qty} × {formatPrice(line.price)}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-primary shrink-0">
                      {formatPrice((Number(line.price) || 0) * (Number(line.qty) || 0))}
                    </span>
                  </div>
                ))}

                {/* Garanties Atelier : listées à part des articles, parce
                    qu'elles n'en sont pas — elles couvrent une ligne existante.
                    Le libellé affiché est celui servi par le serveur. */}
                {warranties.map((warranty) => (
                  <div
                    key={`garantie-${warranty.target_product_id}`}
                    className="flex items-start justify-between gap-3 p-3"
                    data-testid={`special-order-warranty-${warranty.target_product_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-primary line-clamp-2">
                        Garantie Atelier — {warranty.product_name}
                      </p>
                      <p className="text-xs text-slate-600">
                        {warranty.service_label
                          ? `${warranty.service_label} · `
                          : ''}{warranty.qty} × {formatPrice(warranty.price)}
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-primary shrink-0">
                      {formatPrice(Number(warranty.price) * warranty.qty)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              /* Récap produit + délai */
              <div className="bg-slate-50 rounded-sm border border-slate-200 p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-sm bg-secondary/10 flex items-center justify-center shrink-0">
                  <span className="text-secondary font-bold text-sm">{product.price?.toFixed(0)}€</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary line-clamp-1">{product.name}</p>
                  <p className="text-xs text-slate-600">{delayText}</p>
                </div>
              </div>
            )}

            {/* Récap prix. Le montant vient du serveur dès qu'il a répondu : le
                total du panier est recalculé en base, il peut différer de
                l'instantané affiché jusque-là. */}
            <div className="bg-secondary/5 border border-secondary/15 rounded-sm p-4">
              {!isCart && (
                <>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Prix TTC</span>
                    <span className="text-primary font-medium">{formatPrice(product.price)}</span>
                  </div>
                  <div className="border-t border-slate-200 my-2" />
                </>
              )}
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-secondary">{isCart ? 'Total TTC' : 'Total à régler'}</span>
                <span className="text-secondary" data-testid="special-order-total">
                  {formatPrice(displayTotal)}
                </span>
              </div>
            </div>

            {paymentReady && needsTotalAck ? (
              /* ── Écart de montant : prise d'acte AVANT tout moyen de paiement ──
                 Les sous-totaux ci-dessus viennent de l'instantané ; le total,
                 lui, a été recalculé en base. Laisser le client passer au
                 paiement sans le dire, c'est lui faire régler une somme qu'il
                 n'a pas validée — en vente à distance, ce n'est pas négociable. */
              <div className="space-y-4" data-testid="special-order-total-changed" role="alert">
                <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 text-sm text-amber-900 space-y-1">
                  <p className="font-semibold">Le montant a changé</p>
                  <p>
                    Les prix ont été revérifiés au moment de la commande : le total passe
                    de {formatPrice(quotedTotal)} à {formatPrice(serverTotal)}. C'est ce
                    dernier montant qui vous sera demandé.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setTotalAcknowledged(true)}
                  data-testid="special-order-accept-total"
                  className="w-full bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3.5 text-sm transition-colors"
                >
                  J'accepte ce montant et je paie {formatPrice(depositAmount)}
                </button>
                {sortiePaiement}
              </div>
            ) : showPaymentStep ? (
              showPaypalStep ? (
                /* ── Étape 2 : paiement PayPal (« Paiement en 4× » via paylater) ── */
                <div className="space-y-4" data-testid="special-order-paypal">
                  {error && (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-3 text-sm text-red-500" data-testid="special-order-payment-error" role="alert">
                      {error}
                    </div>
                  )}
                  <PayPalScriptProvider
                    options={{
                      clientId: paypalClientId,
                      currency: 'EUR',
                      components: 'buttons',
                      'enable-funding': 'paylater,card',
                    }}
                  >
                    <PayPalButtons
                      style={{ layout: 'vertical', label: 'pay' }}
                      createOrder={() => Promise.resolve(paypalOrderId)}
                      onApprove={async (data) => {
                        try {
                          await capturePaypalOrder(data.orderID);
                          handlePaid();
                        } catch (err) {
                          setError(err.message || "Le paiement a été approuvé mais la confirmation a échoué. Contactez la boutique.");
                        }
                      }}
                      onCancel={() => setError(
                        "Paiement interrompu : rien n'a été débité. Votre commande reste "
                        + 'enregistrée et non réglée — reprenez le paiement ci-dessus.',
                      )}
                      onError={() => setError('Le paiement PayPal a échoué. Réessayez ou contactez la boutique.')}
                    />
                  </PayPalScriptProvider>
                  <p className="text-[10px] text-slate-500 leading-relaxed text-center">
                    Total de {formatPrice(depositAmount)} — paiement sécurisé par PayPal.
                  </p>
                  {sortiePaiement}
                </div>
              ) : (
                /* ── Étape 2 : paiement Stripe ── */
                <div className="space-y-4">
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <StripePaymentForm depositAmount={depositAmount} onPaid={handlePaid} />
                  </Elements>
                  {sortiePaiement}
                </div>
              )
            ) : (
              /* ── Étape 1 : coordonnées + conditions ── */
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="special-order-form">
                {isMaintenanceActive && (
                  <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-3 text-sm text-slate-700">
                    {maintenanceMessage}
                  </div>
                )}

                {isAuthenticated ? (
                  hasCompleteProfile ? (
                    <div className="space-y-4">
                      <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-3">
                        <div className="flex items-center gap-2 text-sm text-primary">
                          <UserCircle2 className="w-4 h-4 text-secondary" />
                          Commande avec votre profil
                        </div>
                        <div className="text-sm text-slate-700 space-y-1">
                          <p>{user.first_name} {user.last_name}</p>
                          <p>{user.email}</p>
                          <p>{user.phone}</p>
                        </div>
                        <div className="text-xs text-slate-500">
                          {[user.address, user.postal_code, user.city, user.country].filter(Boolean).join(', ')}
                        </div>
                      </div>

                      <div className="flex items-start gap-3 bg-secondary/10 border border-secondary/30 rounded-sm p-4 text-sm text-slate-700">
                        <ShieldCheck className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                        Vos coordonnées seront réutilisées automatiquement. Si elles ne sont plus à jour, modifiez-les avant de confirmer.
                      </div>

                      <Link to="/compte/profil" className="inline-flex items-center gap-2 text-sm text-secondary hover:text-secondary transition-colors">
                        Modifier mes coordonnées
                      </Link>
                    </div>
                  ) : (
                    <div className="bg-secondary/10 border border-secondary/30 rounded-sm p-4 text-sm text-slate-700 space-y-3">
                      <p>Votre compte est reconnu, mais votre profil client doit être complété avant de commander sans ressaisie.</p>
                      <Link
                        to={nextProfilePath}
                        className="inline-flex items-center gap-2 bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm px-4 py-2 transition-colors"
                      >
                        Compléter mon profil
                      </Link>
                    </div>
                  )
                ) : (
                  <>
                    <div className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700">
                      <UserCircle2 className="w-5 h-5 text-secondary shrink-0 mt-0.5" />
                      <div className="space-y-2">
                        <p>Vous pouvez commander en invité, ou vous connecter pour réutiliser vos coordonnées et retrouver votre historique.</p>
                        <Link
                          to={`/compte/inscription?next=${encodeURIComponent(returnPath)}`}
                          className="inline-flex items-center gap-2 text-secondary hover:text-secondary transition-colors"
                        >
                          Se connecter ou créer mon compte
                        </Link>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <label className="space-y-1.5">
                        <span className="block text-xs text-slate-600">Prénom</span>
                        <input
                          type="text"
                          required
                          value={form.first_name}
                          onChange={(event) => setForm({ ...form, first_name: event.target.value })}
                          className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
                          placeholder="Jean"
                        />
                      </label>
                      <label className="space-y-1.5">
                        <span className="block text-xs text-slate-600">Nom</span>
                        <input
                          type="text"
                          required
                          value={form.last_name}
                          onChange={(event) => setForm({ ...form, last_name: event.target.value })}
                          className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
                          placeholder="Dupont"
                        />
                      </label>
                    </div>

                    <label className="space-y-1.5 block">
                      <span className="block text-xs text-slate-600">Email</span>
                      <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(event) => setForm({ ...form, email: event.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
                        placeholder="jean.dupont@email.fr"
                      />
                    </label>

                    <label className="space-y-1.5 block">
                      <span className="block text-xs text-slate-600">Téléphone</span>
                      <input
                        type="tel"
                        required
                        value={form.phone}
                        onChange={(event) => setForm({ ...form, phone: event.target.value })}
                        className="w-full bg-slate-50 border border-slate-200 rounded-sm px-4 py-3 text-sm text-primary placeholder-slate-400 outline-none focus:border-secondary"
                        placeholder="06 12 34 56 78"
                      />
                    </label>
                  </>
                )}

                {/* Acceptation des CGV — case OBLIGATOIRE */}
                <label className="flex items-start gap-3 bg-slate-50 border border-slate-200 rounded-sm p-4 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accepted}
                    onChange={(event) => setAccepted(event.target.checked)}
                    className="mt-0.5 w-4 h-4 shrink-0 accent-secondary"
                    data-testid="special-order-accept"
                  />
                  <span className="text-xs text-slate-600 leading-relaxed">
                    J'ai lu et j'accepte les{' '}
                    <a href="/cgv" target="_blank" rel="noopener noreferrer" className="text-secondary hover:underline">
                      conditions générales de vente
                    </a>.
                  </span>
                </label>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-sm p-3 text-sm text-red-500" data-testid="special-order-error" role="alert">
                    {error}
                  </div>
                )}

                <p className="text-[10px] text-slate-500 leading-relaxed">
                  Données utilisées uniquement pour gérer la commande.{' '}
                  <a href="/confidentialite" className="text-secondary/80 hover:text-secondary">En savoir plus</a>
                </p>

                <button
                  type="submit"
                  disabled={loading || isMaintenanceActive || !accepted || (isAuthenticated && !hasCompleteProfile)}
                  className="w-full bg-white text-primary border-2 border-secondary hover:bg-secondary hover:text-white font-bold uppercase tracking-wider rounded-sm py-3.5 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="special-order-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {isCart ? 'Validation en cours…' : 'Préparation du paiement…'}
                    </>
                  ) : (
                    /* En panier, le mode de règlement n'est pas encore connu :
                       c'est le serveur qui décide entre réservation et paiement.
                       Annoncer « Payer » ici serait un pronostic. */
                    isCart ? 'Confirmer ma commande' : `Payer ${formatPrice(depositAmount)}`
                  )}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
